import {
  bookVariantStateV1Schema,
  type BookVariantCommandV1,
  type BookVariantStateV1,
} from "@lifescapsule/contracts";
import { apiRequest } from "../../shared/api";
import { replayableStudioCommand } from "../studio/studio-command-replay";
import type { LibraryPhoto } from "../photos/photo-fixtures";

/**
 * The variant state's address. This client reads version 3 (the comic hero's look and, since
 * 2026-11-02, the comic's story in its settings); the server sends those only to clients that ask,
 * so older ones keep working (P2-7).
 */
function variantPath(bookId: string, nativeReceipts: boolean) {
  const query = new URLSearchParams({ responseVersion: "3" });
  if (nativeReceipts) query.set("nativeReceipts", "1");
  return `/books/${bookId}/variant?${query.toString()}`;
}

/** Where the book's pictures and proof stand. */
export async function loadVariantState(bookId: string, idToken: string, nativeReceipts = false) {
  const result = await apiRequest<{ data: unknown }>(variantPath(bookId, nativeReceipts), idToken);
  return bookVariantStateV1Schema.parse(result.data);
}

/**
 * Sends one step's command and returns the book as it stands after it. Paid steps carry an
 * attempt key, so a retried request never paints twice.
 */
export async function sendVariantCommand(
  bookId: string,
  idToken: string,
  command: BookVariantCommandV1,
  attemptKey?: string,
  nativeReceipts = false,
): Promise<BookVariantStateV1> {
  // Compile and proof rendering now have original-operation receipts too. All callers, including
  // the older guided steps, retain one account/book/payload-bound identity after a lost reply.
  if (!attemptKey && (command.action === "COMPILE" || command.action === "RENDER_PROOF"))
    return sendReplayableVariantCommand(bookId, idToken, command);
  const result = await apiRequest<{ data: unknown }>(variantPath(bookId, nativeReceipts), idToken, {
    method: "POST",
    headers: attemptKey ? { "idempotency-key": attemptKey } : {},
    body: JSON.stringify(command),
  });
  return bookVariantStateV1Schema.parse(result.data);
}

/** The script, chapter, recipe or picture place a command is about, when it names one. */
function variantCommandSourceUnit(command: BookVariantCommandV1) {
  const fields = command as Record<string, unknown>;
  for (const field of ["scriptId", "chapterId", "recipeId", "slotId", "useId"])
    if (typeof fields[field] === "string") return `${field}:${String(fields[field])}`;
  return undefined;
}

/**
 * A command the server requires a key for and replays once committed (engine 76d30ac9: the
 * comic script's save and approval): the same command, from the same account, keeps its first
 * key across a retry.
 */
export async function sendReplayableVariantCommand(
  bookId: string,
  idToken: string,
  command: BookVariantCommandV1,
  validate: (state: BookVariantStateV1) => BookVariantStateV1 = (state) => state,
  nativeReceipts = false,
): Promise<BookVariantStateV1> {
  return replayableStudioCommand({
    idToken,
    bookId,
    scope: `variant:${command.action}`,
    sourceUnit: variantCommandSourceUnit(command),
    command,
    write: (idempotencyKey) =>
      sendVariantCommand(bookId, idToken, command, idempotencyKey, nativeReceipts),
    validate,
  });
}

/** Keep an unconfirmed Comic action's retry key until its exact result is confirmed. */
export async function sendComicEditorCommand(
  bookId: string,
  idToken: string,
  command: BookVariantCommandV1,
): Promise<BookVariantStateV1> {
  return sendReplayableVariantCommand(bookId, idToken, command, (state) => {
    if (
      state.variant !== "COMIC" ||
      !state.script ||
      (command.action === "SAVE_SCRIPT"
        ? JSON.stringify(state.script.script) !== JSON.stringify(command.script)
        : command.action === "APPROVE_SCRIPT"
          ? state.script.scriptId !== command.scriptId || !state.script.approvedAt
          : true)
    )
      throw new Error("The reply did not confirm this Comic action.");
    return state;
  });
}

/** The photos added to this book, newest first, ready to place. */
export async function loadBookPhotos(bookId: string, idToken: string) {
  const photos: LibraryPhoto[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < 6; page += 1) {
    const query = new URLSearchParams({ limit: "48" });
    if (cursor) query.set("cursor", cursor);
    const result = await apiRequest<{
      data: LibraryPhoto[];
      meta?: { cursor?: string; hasMore?: boolean };
    }>(`/books/${bookId}/photos?${query.toString()}`, idToken);
    photos.push(...result.data);
    if (!result.meta?.hasMore || !result.meta.cursor || result.meta.cursor === cursor) break;
    cursor = result.meta.cursor;
  }
  return photos.filter((photo) => photo.processingState === "READY");
}

/** Adds one of the family's photos to this book; adding it again changes nothing. */
export async function attachBookPhoto(bookId: string, idToken: string, photoId: string) {
  await apiRequest(`/books/${bookId}/photos/${photoId}`, idToken, { method: "POST" });
}
